import {EventEmitter} from "eventemitter3";

import {
  DesyncPayload,
  PlayerController,
  PlayerControllerEvents,
} from "./PlayerController.js";

/**
 * A controller for a remote player that supports rollback netcode.
 * It predicts future actions and emits a 'desync-detected' event when a
 * prediction is proven wrong by new data from the network.
 */
export class RemotePlayerController
  extends EventEmitter<PlayerControllerEvents>
  implements PlayerController
{
  public readonly uid: string;

  // The complete, confirmed list of actions from the network.
  private confirmedActions: boolean[] = [];
  // A cache of actions we have provided to the simulation, including predictions.
  private simulatedActions: boolean[] = [];

  constructor(
    uid: string,
    public ownerId: string,
  ) {
    super();
    this.uid = uid;
  }

  public getAllActions(): boolean[] {
    // Return the confirmed actions, as this is the "source of truth".
    return this.confirmedActions;
  }

  /**
   * The network layer calls this when a new, complete action list arrives.
   * This is where misprediction detection happens.
   */
  public onRemoteActionsReceived(newActions: boolean[]): void {
    // Find the first step where our simulation differed from the new truth.
    let firstMismatchStep = -1;

    for (let i = 0; i < newActions.length; i++) {
      if (
        i < this.simulatedActions.length &&
        this.simulatedActions[i] !== newActions[i]
      ) {
        // We found a misprediction!
        firstMismatchStep = i;
        break;
      }
    }

    // Now, update our internal state with the new source of truth.
    this.confirmedActions = newActions;

    // If we found a mismatch, emit the event to trigger a rollback.
    if (firstMismatchStep !== -1) {
      const payload: DesyncPayload = {
        rollbackToStep: firstMismatchStep,
      };
      this.emit("desync-detected", payload);
    }
  }

  /**
   * Called by the SimulationManager at each step.
   * It returns a confirmed action if available, otherwise it predicts.
   */
  public getActionForStep(step: number): boolean {
    let action: boolean;

    if (step < this.confirmedActions.length) {
      // We have a confirmed action for this step. Use it.
      action = this.confirmedActions[step]!;
    } else {
      // We are in prediction territory.
      // Simple prediction: repeat the last known action.
      action = this.confirmedActions[this.confirmedActions.length - 1] ?? false;
    }

    // Store the action we provided to the simulation so we can check it later.
    this.simulatedActions[step] = action;
    return action;
  }

  /**
   * Resets the controller for a new round.
   */
  public reset(): void {
    this.confirmedActions = [];
    this.simulatedActions = [];
  }
}
